import { useEffect, useState } from "react";
import { MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inputCls } from "@/lib/billing";
import { Card } from "@/components/PortalPage";

// Customer-facing (light) opt-in on the quote, invoice and change order pages.
export const SmsConsent = ({ token }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api(`/billing/public/${token}/sms`, { auth: false }).then(setS).catch(() => {}); }, [token]);
  if (!s?.available) return null;
  const set = async (agree) => {
    setBusy(true);
    try { const r = await api(`/billing/public/${token}/sms-consent`, { method: "POST", auth: false, body: { agree } }); setS({ ...s, opted_in: agree, needs_start: r.needs_start }); } catch (e) { window.alert(e.message); } finally { setBusy(false); }
  };
  return (
    <label className="flex items-start gap-2 rounded-xl bg-white p-4 text-xs text-[#444]" data-testid="public-sms-consent">
      <input type="checkbox" disabled={busy} checked={s.opted_in} onChange={(e) => set(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#2f8f00]" data-testid="public-sms-consent-check" />
      <span><b>Text me updates at the number ending {s.phone_last4}.</b> {s.wording}{s.needs_start && <b className="mt-1 block text-[#8a3b00]" data-testid="public-sms-needs-start">You texted STOP earlier. To finish turning texts back on, text START to the number we texted you from.</b>}</span>
    </label>
  );
};

const STATE = { opted_in: ["Agreed to texts", "#4CCC06"], opted_out: ["Texts turned off", "#EF4444"] };

export const CustomerTexting = ({ customer }) => {
  const [c, setC] = useState(customer.sms_consent || null);
  const [note, setNote] = useState("");
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api("/billing/sms/status").then(setCfg).catch(() => {}); }, []);
  const save = async (status) => {
    try { const r = await api(`/billing/customers/${customer.id}/sms-consent`, { method: "POST", body: { status, note } }); setC({ ...r, changed_at: new Date().toISOString() }); setNote(""); toast.success(status === "opted_in" ? "Agreement recorded" : "Texts turned off"); }
    catch (e) { toast.error(e.message); }
  };
  const [label, color] = STATE[c?.status] || ["Hasn't agreed to texts", "#888"];
  return (
    <Card className="mt-4 space-y-2" data-testid="customer-texting">
      <p className="flex items-center gap-2 text-xs font-extrabold tracking-[1.5px] text-neutral-400"><MessageSquare size={14} />TEXT MESSAGES</p>
      <p className="text-sm font-bold" style={{ color }} data-testid="customer-texting-status">{label}{c?.phone_last4 || c?.phone ? ` · number ending ${(c.phone_last4 || c.phone).slice(-4)}` : ""}</p>
      {cfg && !cfg.configured && <p className="text-[11px] text-[#F59E0B]" data-testid="customer-texting-off">{cfg.reason || "Texting isn't set up yet."} No texts go out until then.</p>}
      <p className="text-[11px] text-[#888]">Customers usually agree by ticking the box on their quote. If they agreed another way, write how below.</p>
      <input className={inputCls} placeholder="How they agreed, e.g. signed paper form on 10/27" value={note} onChange={(e) => setNote(e.target.value)} data-testid="customer-texting-note" />
      <div className="flex gap-2 text-[11px] font-bold">
        <button onClick={() => save("opted_in")} disabled={note.trim().length < 5} data-testid="customer-texting-optin" className="rounded-full border border-[var(--accent)] px-3 py-1 text-[var(--accent)] disabled:opacity-40">Record agreement</button>
        {c?.status !== "opted_out" && <button onClick={() => save("opted_out")} data-testid="customer-texting-optout" className="rounded-full border border-[#552222] px-3 py-1 text-[#ff8080]">Turn texts off</button>}
      </div>
    </Card>
  );
};

export const TextLink = ({ doc }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  if (["draft", "void"].includes(doc.status)) return null;
  const send = async () => {
    setBusy(true);
    try { await api(`/billing/docs/${doc.id}/text`, { method: "POST", body: { message: msg } }); setMsg(""); toast.success("Text sent. Delivery shows on the timeline when the carrier confirms it."); }
    catch (e) { toast.warning(e.message); } finally { setBusy(false); }
  };
  return (
    <Card className="mt-4 flex flex-wrap items-center gap-2" data-testid="doc-text-link">
      <MessageSquare size={16} className="text-[var(--accent)]" />
      <input className={`${inputCls} min-w-[200px] flex-1`} placeholder="Optional short note for the text" maxLength={300} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="doc-text-message" />
      <button onClick={send} disabled={busy} data-testid="doc-text-alr-btn" className="rounded-lg border border-[var(--accent)] px-4 py-2.5 text-xs font-extrabold tracking-[1px] text-[var(--accent)] disabled:opacity-40">SEND TEXT FROM ALR (TRACKED)</button>
    </Card>
  );
};
